import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import Footer from '../components/Footer';
import '../styles/CronExpressionPage.css';

// ============================================================
// Timezone helpers
// ============================================================

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Get time components (minute/hour/dom/month/dow) for a Date in any IANA timezone.
function getTimePartsInTz(date, tz) {
  if (tz === 'UTC') {
    return {
      minute: date.getUTCMinutes(),
      hour:   date.getUTCHours(),
      dom:    date.getUTCDate(),
      month:  date.getUTCMonth() + 1,
      dow:    date.getUTCDay(),
    };
  }
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', weekday: 'short',
    hour12: false,
  }).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
  return {
    minute: parseInt(parts.minute, 10),
    hour:   parseInt(parts.hour, 10) % 24,   // guard against "24" at midnight
    dom:    parseInt(parts.day, 10),
    month:  parseInt(parts.month, 10),
    dow:    DOW_SHORT.indexOf(parts.weekday),
  };
}

// Get the short abbreviation for any IANA timezone (e.g. "ET", "IST", "UTC").
function getTzAbbr(tz) {
  if (tz === 'UTC') return 'UTC';
  const resolved = tz === '__local__' ? Intl.DateTimeFormat().resolvedOptions().timeZone : tz;
  return new Date().toLocaleTimeString('en-US', { timeZone: resolved, timeZoneName: 'short' }).split(' ').pop();
}

// Resolve '__local__' to the browser's IANA timezone string.
function resolveInputTz(tz) {
  return tz === '__local__' ? Intl.DateTimeFormat().resolvedOptions().timeZone : tz;
}

// ============================================================
// Cron utilities
// ============================================================

function normalizeField(f) { return f === '?' ? '*' : f; }

function matchesField(val, expr) {
  expr = normalizeField(String(expr).trim());
  if (expr === '*') return true;
  if (/^\*\/\d+$/.test(expr)) { const n = parseInt(expr.slice(2), 10); return n > 0 && val % n === 0; }
  if (/^\d+-\d+$/.test(expr)) { const [a, b] = expr.split('-').map(Number); return val >= a && val <= b; }
  if (/^[\d,]+$/.test(expr)) return expr.split(',').map(Number).includes(val);
  if (/^\d+$/.test(expr)) return val === parseInt(expr, 10);
  return false;
}

// 5-field standard, 6-field Quartz (sec prefix), 7-field Quartz (sec + year)
function parseCronFields(expr) {
  const parts = String(expr).trim().split(/\s+/);
  let min, hour, dom, month, dow;
  if (parts.length === 5) { [min, hour, dom, month, dow] = parts; }
  else if (parts.length === 6) { [, min, hour, dom, month, dow] = parts; }
  else if (parts.length === 7) { [, min, hour, dom, month, dow] = parts; }
  else { return null; }
  return {
    minute: normalizeField(min),  hour:  normalizeField(hour),
    dom:    normalizeField(dom),  month: normalizeField(month),
    dow:    normalizeField(dow),
    _format: parts.length === 5 ? 'standard' : 'quartz',
  };
}

const MONTH_NAMES     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES       = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const FULL_DAY_NAMES  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function pad(n) { return String(n).padStart(2, '0'); }

function fieldMeaning(value, type) {
  if (value === '*') return 'Every ' + type;
  if (/^\*\/\d+$/.test(value)) return 'Every ' + value.slice(2) + ' ' + type + '(s)';
  if (/^\d+-\d+$/.test(value)) {
    const [a, b] = value.split('-').map(Number);
    if (type === 'hour')    return pad(a) + ':00 – ' + pad(b) + ':59';
    if (type === 'month')   return MONTH_NAMES[a-1] + ' – ' + MONTH_NAMES[b-1];
    if (type === 'weekday') return DAY_NAMES[a] + ' – ' + DAY_NAMES[b];
    return value;
  }
  if (/^[\d,]+$/.test(value)) {
    const nums = value.split(',').map(Number);
    if (type === 'month')   return nums.map((n) => MONTH_NAMES[n-1]).join(', ');
    if (type === 'weekday') return nums.map((n) => DAY_NAMES[n]).join(', ');
    return value;
  }
  if (/^\d+$/.test(value)) {
    const n = parseInt(value, 10);
    if (type === 'hour')    return pad(n) + ':00';
    if (type === 'minute')  return 'at minute ' + n;
    if (type === 'month')   return MONTH_NAMES[n-1] || value;
    if (type === 'weekday') return DAY_NAMES[n] || value;
    return value;
  }
  return value;
}

function ordinal(n) {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
}

// tzLabel is a short string like 'UTC', 'ET', 'IST' — appended to time descriptions.
// No conversion is done here; the cron fields are interpreted as-is in that timezone.
function decodeCronToText(expr, tzLabel = 'UTC') {
  expr = String(expr).trim();
  const fields = parseCronFields(expr);
  if (!fields) return { valid: false, description: 'Invalid cron expression — must have 5, 6, or 7 fields.', fields: null };

  const { minute, hour, dom, month, dow } = fields;
  const fieldRe = /^(\*|\*\/\d+|\d+(-\d+)?(,\d+(-\d+)?)*)$/;
  for (const f of [minute, hour, dom, month, dow]) {
    if (!fieldRe.test(f))
      return { valid: false, description: 'Invalid cron expression — unrecognized field: "' + f + '"', fields: null };
  }

  let desc = '';

  if (/^\*\/\d+$/.test(minute)) {
    const n = parseInt(minute.slice(2), 10);
    desc = 'Every ' + n + ' minute' + (n !== 1 ? 's' : '');
  } else if (minute === '*' && hour === '*') {
    desc = 'Every minute';
  } else if (/^\d+$/.test(minute) && /^\d+$/.test(hour)) {
    const m = parseInt(minute, 10);
    const h = parseInt(hour, 10);
    if (h === 0 && m === 0) {
      desc = 'At 12:00 AM ' + tzLabel + ' (midnight)';
    } else if (h === 12 && m === 0) {
      desc = 'At 12:00 PM ' + tzLabel + ' (noon)';
    } else {
      const ampm   = h < 12 ? 'AM' : 'PM';
      const dispH  = h === 0 ? 12 : h > 12 ? h - 12 : h;
      desc = 'At ' + pad(dispH) + ':' + pad(m) + ' ' + ampm + ' ' + tzLabel;
    }
  } else if (minute === '*' && /^\d+$/.test(hour)) {
    const h = parseInt(hour, 10);
    const ampm  = h < 12 ? 'AM' : 'PM';
    const dispH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    desc = 'Every minute of ' + dispH + ' ' + ampm + ' ' + tzLabel;
  } else {
    desc = 'At minute ' + minute;
    if (/^\*\/\d+$/.test(hour)) { desc += ', every ' + hour.slice(2) + ' hour(s)'; }
    else if (hour !== '*')       { desc += ' of hour ' + hour + ' ' + tzLabel; }
  }

  const dayParts = [];
  if (dow !== '*') {
    if      (/^\d+-\d+$/.test(dow)) { const [a,b]=dow.split('-').map(Number); dayParts.push(FULL_DAY_NAMES[a]+' through '+FULL_DAY_NAMES[b]); }
    else if (/^[\d,]+$/.test(dow))  { dayParts.push(dow.split(',').map((d)=>FULL_DAY_NAMES[parseInt(d,10)]).join(', ')); }
    else if (/^\d+$/.test(dow))     { dayParts.push('on '+FULL_DAY_NAMES[parseInt(dow,10)]); }
  }
  if (dom !== '*') {
    if (/^\d+$/.test(dom)) { dayParts.push('on the '+dom+ordinal(parseInt(dom,10))+' of the month'); }
    else                   { dayParts.push('on day '+dom); }
  }
  if (month !== '*') {
    if (/^\d+$/.test(month))      { dayParts.push('in '+(MONTH_NAMES[parseInt(month,10)-1]||month)); }
    else if (/^\d+-\d+$/.test(month)) { const [a,b]=month.split('-').map(Number); dayParts.push('from '+MONTH_NAMES[a-1]+' to '+MONTH_NAMES[b-1]); }
    else                           { dayParts.push('in month '+month); }
  }
  if (dayParts.length > 0) {
    desc += ', ' + dayParts.join(', ');
  } else if (dom === '*' && dow === '*') {
    if (!/every minute/i.test(desc) && !/Every \d+ minute/i.test(desc)) desc += ', every day';
  }

  return {
    valid: true,
    description: desc,
    fields: {
      minute: { raw: minute, meaning: fieldMeaning(minute, 'minute') },
      hour:   { raw: hour,   meaning: fieldMeaning(hour,   'hour')   },
      dom:    { raw: dom,    meaning: fieldMeaning(dom,    'day')    },
      month:  { raw: month,  meaning: fieldMeaning(month,  'month')  },
      dow:    { raw: dow,    meaning: fieldMeaning(dow,    'weekday')},
    },
  };
}

// Cron fields are matched against time in `inputTz`. The returned Date objects are UTC instants.
function getNextRuns(expr, count = 5, inputTz = 'UTC') {
  const fields = parseCronFields(expr);
  if (!fields) return [];
  const { minute, hour, dom, month, dow } = fields;
  const tz = resolveInputTz(inputTz);

  const results = [];
  const cursor = new Date();
  cursor.setUTCSeconds(0, 0);
  cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);

  let tries = 0;
  while (results.length < count && tries < 525600) {
    tries++;
    const p = getTimePartsInTz(cursor, tz);
    if (
      matchesField(p.minute, minute) &&
      matchesField(p.hour,   hour)   &&
      matchesField(p.dom,    dom)    &&
      matchesField(p.month,  month)  &&
      matchesField(p.dow,    dow)
    ) {
      results.push(new Date(cursor));
    }
    cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  }
  return results;
}

// Display a Date object in UTC or browser-local timezone.
function formatRunDate(date, useUTC) {
  if (useUTC) {
    const h = date.getUTCHours(), m = date.getUTCMinutes();
    const ampm = h < 12 ? 'AM' : 'PM';
    const dH   = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${DAY_NAMES[date.getUTCDay()]}, ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCDate()} ${date.getUTCFullYear()}, ${pad(dH)}:${pad(m)} ${ampm} UTC`;
  }
  const h = date.getHours(), m = date.getMinutes();
  const ampm = h < 12 ? 'AM' : 'PM';
  const dH   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const tzAbbr = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
  return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}, ${pad(dH)}:${pad(m)} ${ampm} ${tzAbbr}`;
}

function getNYTime(date) {
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function getUTCTime(date) {
  const h = date.getUTCHours(), m = date.getUTCMinutes(), s = date.getUTCSeconds();
  const ampm = h < 12 ? 'AM' : 'PM';
  const dH   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${DAY_NAMES[date.getUTCDay()]}, ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCDate()} ${date.getUTCFullYear()}, ${pad(dH)}:${pad(m)}:${pad(s)} ${ampm}`;
}

// ============================================================
// Constants
// ============================================================

const TIMEZONES = [
  { label: 'UTC',                         value: 'UTC' },
  { label: 'Browser Local',               value: '__local__' },
  { label: '── Americas ──',              value: '__sep1__', disabled: true },
  { label: 'US Eastern (ET)',             value: 'America/New_York' },
  { label: 'US Central (CT)',             value: 'America/Chicago' },
  { label: 'US Mountain (MT)',            value: 'America/Denver' },
  { label: 'US Pacific (PT)',             value: 'America/Los_Angeles' },
  { label: 'US Alaska (AKT)',             value: 'America/Anchorage' },
  { label: 'US Hawaii (HST)',             value: 'Pacific/Honolulu' },
  { label: 'Brazil (BRT)',                value: 'America/Sao_Paulo' },
  { label: '── Europe ──',               value: '__sep2__', disabled: true },
  { label: 'London (GMT/BST)',            value: 'Europe/London' },
  { label: 'Paris / Berlin (CET/CEST)',   value: 'Europe/Paris' },
  { label: 'Helsinki (EET/EEST)',         value: 'Europe/Helsinki' },
  { label: 'Moscow (MSK)',               value: 'Europe/Moscow' },
  { label: '── Asia / Pacific ──',       value: '__sep3__', disabled: true },
  { label: 'Dubai (GST)',                 value: 'Asia/Dubai' },
  { label: 'India (IST)',                 value: 'Asia/Kolkata' },
  { label: 'Bangladesh (BST)',            value: 'Asia/Dhaka' },
  { label: 'Thailand (ICT)',              value: 'Asia/Bangkok' },
  { label: 'China / Singapore (CST/SGT)', value: 'Asia/Shanghai' },
  { label: 'Japan / Korea (JST/KST)',     value: 'Asia/Tokyo' },
  { label: 'Australia Eastern (AEST)',    value: 'Australia/Sydney' },
  { label: 'New Zealand (NZST/NZDT)',     value: 'Pacific/Auckland' },
];

const FIELD_CONFIGS = [
  {
    key: 'minute', label: 'Minute', description: 'Minute of the hour (0–59)',
    presets: [
      { label: 'Every minute (*)',   value: '*'     },
      { label: 'Every 5 min (*/5)',  value: '*/5'   },
      { label: 'Every 10 min (*/10)',value: '*/10'  },
      { label: 'Every 15 min (*/15)',value: '*/15'  },
      { label: 'Every 30 min (*/30)',value: '*/30'  },
      { label: 'Custom',             value: '__custom__' },
    ],
  },
  {
    key: 'hour', label: 'Hour', description: 'Hour of the day (0–23)',
    presets: [
      { label: 'Every hour (*)',    value: '*'     },
      { label: 'Every 2hr (*/2)',   value: '*/2'   },
      { label: 'Every 4hr (*/4)',   value: '*/4'   },
      { label: 'Every 6hr (*/6)',   value: '*/6'   },
      { label: 'Every 12hr (*/12)', value: '*/12'  },
      { label: 'Custom',            value: '__custom__' },
    ],
  },
  {
    key: 'dom', label: 'Day of Month', description: 'Day of month (1–31)',
    presets: [
      { label: 'Every day (*)', value: '*'          },
      { label: '1st (1)',       value: '1'           },
      { label: '15th (15)',     value: '15'          },
      { label: 'Last (28)',     value: '28'          },
      { label: 'Custom',        value: '__custom__' },
    ],
  },
  {
    key: 'month', label: 'Month', description: 'Month (1–12)',
    presets: [
      { label: 'Every month (*)', value: '*'          },
      { label: 'Jan–Jun (1-6)',   value: '1-6'        },
      { label: 'Jul–Dec (7-12)',  value: '7-12'       },
      { label: 'Custom',          value: '__custom__' },
    ],
  },
  {
    key: 'dow', label: 'Day of Week', description: 'Day of week (0=Sun, 6=Sat)',
    presets: [
      { label: 'Every day (*)',   value: '*'          },
      { label: 'Weekdays (1-5)', value: '1-5'        },
      { label: 'Weekends (0,6)', value: '0,6'        },
      { label: 'Monday (1)',     value: '1'           },
      { label: 'Friday (5)',     value: '5'           },
      { label: 'Custom',         value: '__custom__' },
    ],
  },
];

const EXAMPLE_EXPRESSIONS = [
  '* * * * *', '0 * * * *', '0 0 * * *', '*/15 * * * *', '0 9 * * 1-5', '0 0 1 * *',
];

// ============================================================
// Component
// ============================================================

export default function CronExpressionPage() {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [tab,    setTab]    = useState('decode');
  const [useUTC, setUseUTC] = useState(true);
  const [now,    setNow]    = useState(new Date());

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Decode tab state
  const [decodeInput, setDecodeInput] = useState('0 9 * * 1-5');
  const [hasDecoded,  setHasDecoded]  = useState(false);
  const [inputTz,     setInputTz]     = useState('UTC');

  // Build tab state
  const [buildFields,     setBuildFields]     = useState({ minute: '*', hour: '*', dom: '*', month: '*', dow: '*' });
  const [customValues,    setCustomValues]    = useState({ minute: '', hour: '', dom: '', month: '', dow: '' });
  const [selectedPresets, setSelectedPresets] = useState({ minute: '*', hour: '*', dom: '*', month: '*', dow: '*' });
  const [buildCopied,     setBuildCopied]     = useState(false);

  // Derived timezone values
  const resolvedInputTz = resolveInputTz(inputTz);
  const inputTzLabel    = getTzAbbr(inputTz);

  // Decode result — derived at render time so it reacts to inputTz changes automatically
  const decodeResult = useMemo(
    () => hasDecoded ? decodeCronToText(decodeInput, inputTzLabel) : null,
    [hasDecoded, decodeInput, inputTzLabel]
  );

  // Next runs — derived at render time so they react to both inputTz and display toggle
  const nextRunsDecode = useMemo(
    () => decodeResult?.valid ? getNextRuns(decodeInput, 5, resolvedInputTz) : [],
    [decodeResult, decodeInput, resolvedInputTz]
  );

  const handleDecode = () => setHasDecoded(true);

  const handleExampleClick = (expr) => {
    setDecodeInput(expr);
    setHasDecoded(true);
  };

  // Build tab
  const builtExpr    = [buildFields.minute, buildFields.hour, buildFields.dom, buildFields.month, buildFields.dow].join(' ');
  const builtDecoded = useMemo(() => decodeCronToText(builtExpr, 'UTC'), [builtExpr]);
  const nextRunsBuild = useMemo(
    () => builtDecoded.valid ? getNextRuns(builtExpr, 5, 'UTC') : [],
    [builtDecoded, builtExpr]
  );

  const handlePresetChange = (fieldKey, value) => {
    setSelectedPresets((prev) => ({ ...prev, [fieldKey]: value }));
    if (value !== '__custom__') setBuildFields((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
    setBuildFields((prev) => ({ ...prev, [fieldKey]: value || '*' }));
  };

  const handleCopyBuild = () => {
    navigator.clipboard.writeText(builtExpr).then(() => {
      setBuildCopied(true);
      setTimeout(() => setBuildCopied(false), 2000);
    });
  };

  const estLabel = now.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }).split(' ').pop();

  return (
    <div
      className={`container ${darkMode ? 'dark-theme' : ''}`}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}
    >
      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 1000,
          color: '#fff', fontSize: '1rem', padding: '8px 16px', borderRadius: '6px',
          backgroundColor: '#1976d2', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
      >
        {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
      </button>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: { xs: 2, sm: 0 } }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <Typography variant="h4" component="h1"
          sx={{ fontWeight: 700, color: darkMode ? '#42a5f5' : '#1976d2', textAlign: { xs: 'left', sm: 'center' } }}>
          ⏱️ Cron Expression
        </Typography>
        <Box sx={{ width: { sm: '100px' } }} />
      </Box>

      {/* ── Live Clock + Display Timezone Toggle ── */}
      <div className={`cron-clock-bar${darkMode ? ' dark' : ''}`}>
        <div className="cron-clock-times">
          <div className="cron-clock-item">
            <span className="cron-clock-tz-badge utc">UTC</span>
            <span className="cron-clock-time">{getUTCTime(now)}</span>
          </div>
          <div className="cron-clock-divider" />
          <div className="cron-clock-item">
            <span className="cron-clock-tz-badge est">{estLabel}</span>
            <span className="cron-clock-time">{getNYTime(now)}</span>
          </div>
        </div>
        <div className="cron-tz-toggle-wrap">
          <span className="cron-tz-toggle-label">Show runs in:</span>
          <button className={`cron-tz-btn${useUTC ? ' active' : ''}`}  onClick={() => setUseUTC(true)}>UTC</button>
          <button className={`cron-tz-btn${!useUTC ? ' active' : ''}`} onClick={() => setUseUTC(false)}>Local</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cron-tabs">
        <button className={`cron-tab${tab === 'decode' ? ' active' : ''}`} onClick={() => setTab('decode')}>🔍 Decode</button>
        <button className={`cron-tab${tab === 'build'  ? ' active' : ''}`} onClick={() => setTab('build')}>🔨 Build</button>
      </div>

      {/* ===== DECODE TAB ===== */}
      {tab === 'decode' && (
        <div className="cron-tab-content">

          {/* Input row */}
          <div className="cron-input-row">
            <input
              className="cron-input"
              type="text"
              value={decodeInput}
              onChange={(e) => { setDecodeInput(e.target.value); setHasDecoded(false); }}
              placeholder="e.g. 0 9 * * 1-5"
              onKeyDown={(e) => e.key === 'Enter' && handleDecode()}
            />
            <button className="cron-decode-btn" onClick={handleDecode}>Decode</button>
          </div>

          {/* Cron input timezone selector */}
          <div className="cron-tz-select-row">
            <span className="cron-tz-select-label">Cron Timezone:</span>
            <select
              className="cron-field-select"
              value={inputTz}
              onChange={(e) => setInputTz(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value} disabled={tz.disabled}>{tz.label}</option>
              ))}
            </select>
            <span className="cron-tz-abbr-badge">{inputTzLabel}</span>
          </div>

          {/* Example chips */}
          <div className="example-chips">
            {EXAMPLE_EXPRESSIONS.map((expr) => (
              <button key={expr} className="example-chip" onClick={() => handleExampleClick(expr)}>{expr}</button>
            ))}
          </div>

          {/* Result card */}
          {decodeResult && (
            <div className={`cron-result-card${decodeResult.valid ? '' : ' error'}`}>
              {!decodeResult.valid ? (
                <p className="cron-error">{decodeResult.description}</p>
              ) : (
                <>
                  <p className="cron-description">{decodeResult.description}</p>
                  <div className="field-breakdown">
                    {['minute','hour','dom','month','dow'].map((key, idx) => {
                      const labels = ['Minute','Hour','Day','Month','Weekday'];
                      const field  = decodeResult.fields[key];
                      return (
                        <div key={key} className="field-cell">
                          <div className="field-cell-label">{labels[idx]}</div>
                          <div className="field-cell-raw">{field.raw}</div>
                          <div className="field-cell-meaning">{field.meaning}</div>
                        </div>
                      );
                    })}
                  </div>
                  {nextRunsDecode.length > 0 && (
                    <div className="next-runs-section">
                      <div className="next-runs-title">
                        Next 5 Executions
                        <span className="next-runs-tz-badge">{useUTC ? 'UTC' : 'Local'}</span>
                      </div>
                      <table className="next-runs-table">
                        <thead><tr><th>#</th><th>Date &amp; Time</th></tr></thead>
                        <tbody>
                          {nextRunsDecode.map((d, i) => (
                            <tr key={i}><td>{i+1}</td><td>{formatRunDate(d, useUTC)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== BUILD TAB ===== */}
      {tab === 'build' && (
        <div className="cron-tab-content">
          {FIELD_CONFIGS.map((cfg) => (
            <div key={cfg.key} className="cron-field-row">
              <div className="cron-field-label">
                <span className="cron-field-name">{cfg.label}</span>
                <span className="cron-field-desc">{cfg.description}</span>
              </div>
              <div className="cron-field-controls">
                <select
                  className="cron-field-select"
                  value={selectedPresets[cfg.key]}
                  onChange={(e) => handlePresetChange(cfg.key, e.target.value)}
                >
                  {cfg.presets.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {selectedPresets[cfg.key] === '__custom__' && (
                  <input
                    className="cron-field-custom-input"
                    type="text"
                    placeholder="Custom value…"
                    value={customValues[cfg.key]}
                    onChange={(e) => handleCustomChange(cfg.key, e.target.value)}
                  />
                )}
              </div>
            </div>
          ))}

          <div className="cron-expression-display">
            <span className="cron-expr-label">Cron Expression</span>
            <span className="cron-expr-text">{builtExpr}</span>
            <button className={`cron-copy-btn${buildCopied ? ' copied' : ''}`} onClick={handleCopyBuild}>
              {buildCopied ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>

          {builtDecoded.valid && (
            <div className="cron-result-card">
              <p className="cron-description">{builtDecoded.description}</p>
              <div className="field-breakdown">
                {['minute','hour','dom','month','dow'].map((key, idx) => {
                  const labels = ['Minute','Hour','Day','Month','Weekday'];
                  const field  = builtDecoded.fields[key];
                  return (
                    <div key={key} className="field-cell">
                      <div className="field-cell-label">{labels[idx]}</div>
                      <div className="field-cell-raw">{field.raw}</div>
                      <div className="field-cell-meaning">{field.meaning}</div>
                    </div>
                  );
                })}
              </div>
              {nextRunsBuild.length > 0 && (
                <div className="next-runs-section">
                  <div className="next-runs-title">
                    Next 5 Executions
                    <span className="next-runs-tz-badge">{useUTC ? 'UTC' : 'Local'}</span>
                  </div>
                  <table className="next-runs-table">
                    <thead><tr><th>#</th><th>Date &amp; Time</th></tr></thead>
                    <tbody>
                      {nextRunsBuild.map((d, i) => (
                        <tr key={i}><td>{i+1}</td><td>{formatRunDate(d, useUTC)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
